"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Button from "../../../../components/Button";
import styles from "../css/profileDetails.module.css";
import { getProfileByPatient } from "../../../../api/profile/create_profile";

export default function ProfileDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const patientId = location.state?.patientId;

  const [forms, setForms] = useState([]);
  const [profileNumber, setProfileNumber] = useState("");
  const [practitionerName, setPractitionerName] = useState("");
  const [practiceNumber, setPracticeNumber] = useState("");

  useEffect(() => {
    if (!patientId) {
      navigate("/customer/profiles/split-profiles");
      return;
    }

    let cancelled = false;

    async function loadProfile() {
      try {
        const data = await getProfileByPatient(patientId);
        if (!data || cancelled) return;

        const main = data.main || {};
        const mm = data.mainmember || {};
        const profile = data.profile || {};
        const homeParts = String(main.homeaddress || "")
          .split(",")
          .map((s) => s.trim());
        const d = main.deliveryaddress;
        let delivery = { street: "", suburb: "", city: "", postal: "" };
        if (d && typeof d === "object" && !Array.isArray(d)) {
          const addr = d["Delivery Address 1"] || {};
          delivery = {
            street: addr.street || "",
            suburb: addr.suburb || "",
            city: addr.city || "",
            postal: addr.postalCode || addr.postal || "",
          };
        } else if (d) {
          const parts = String(d)
            .split(",")
            .map((s) => s.trim());
          delivery = {
            street: parts[0] || "",
            suburb: parts[1] || "",
            city: parts[2] || "",
            postal: parts[3] || "",
          };
        }

        const chronic = Array.isArray(main.chronicconditions)
          ? main.chronicconditions.join(", ")
          : main.chronicconditions || "";
        const allergies = Array.isArray(main.allergies)
          ? main.allergies.join(", ")
          : main.allergies || "";

        setProfileNumber(profile.profilenumber || "");
        setPractitionerName(`${mm.practitioner?.title || ""} ${mm.practitioner?.firstname || ""} ${mm.practitioner?.surname || ""}`.trim());
        setPracticeNumber(mm.practitioner?.practicenumber || "");

        setForms([
          {
            id: 1,
            surname: main.surname || "",
            initials: (main.firstname || "").slice(0, 1).toUpperCase(),
            firstNames: main.firstname || "",
            title: main.title || "",
            idNumber: main.idnumber || "",
            sex: main.sex || "",
            age: main.age != null ? String(main.age) : "",
            weight: main.weightinkg != null ? String(main.weightinkg) : "",
            postalAddressLine1: homeParts[0] || "",
            postalAddressLine2: homeParts[1] || "",
            postalAddressLine3: homeParts[2] || "",
            postalAddressLine4: homeParts[3] || "",
            deliveryAddressLine1: delivery.street || "",
            deliveryAddressLine2: delivery.suburb || "",
            deliveryAddressLine3: delivery.city || "",
            deliveryAddressLine4: delivery.postal || "",
            accountNumber: mm.accountnumber || "",
            emailAddress: main.email || "",
            homeCellNumber: main.contactnumber || "",
            employer: mm.employer || "",
            chronicConditions: chronic,
            beneficiaries: mm.beneficiaries != null ? String(mm.beneficiaries) : "",
            allergies: allergies,
            medicalAidProvider: mm.medicalaidprovider || "",
            medicalAidNumber: mm.medicalaidnumber || "",
            note: main.note || "",
          },
        ]);
      } catch (_) {
        navigate("/customer/profiles/split-profiles");
      }
    }

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [navigate, patientId]);

  const handleSplitProfile = () => {
    if (!patientId) {
      navigate("/customer/profiles/split-profiles");
      return;
    }
    navigate("/customer/profile/split-profile", {
      state: { patientId, profile: forms[0] },
    });
  };

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />

        <div className={styles.content}>
          <h2 className={styles.mainTitle}>
            Main Member Profile Number: {profileNumber || ""}
          </h2>

          {forms[0] && (
            <div className={styles.layout}>
              <div className={styles.leftColumn}>
                <div className={styles.field}>
                  <label>Surname</label>
                  <input type="text" value={forms[0].surname} readOnly />
                </div>
                <div className={styles.field}>
                  <label>Initial</label>
                  <input type="text" value={forms[0].initials} readOnly />
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>First Name(s)</label>
                    <input type="text" value={forms[0].firstNames} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Title</label>
                    <input type="text" value={forms[0].title} readOnly />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>ID Number</label>
                  <input type="text" value={forms[0].idNumber} readOnly />
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Sex (M,F)</label>
                    <input type="text" value={forms[0].sex} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Age</label>
                    <input type="text" value={forms[0].age} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Weight (kg)</label>
                    <input type="text" value={forms[0].weight} readOnly />
                  </div>
                </div>
                <div className={styles.addressGroup}>
                  <label>Postal Address</label>
                  <input type="text" value={forms[0].postalAddressLine1} readOnly />
                  <input type="text" value={forms[0].postalAddressLine2} readOnly />
                  <input type="text" value={forms[0].postalAddressLine3} readOnly />
                  <input type="text" value={forms[0].postalAddressLine4} readOnly />
                </div>
              </div>

              <div className={styles.leftColumn}>
                <div className={styles.addressGroup}>
                  <label>Delivery Address</label>
                  <input type="text" value={forms[0].deliveryAddressLine1} readOnly />
                  <input type="text" value={forms[0].deliveryAddressLine2} readOnly />
                  <input type="text" value={forms[0].deliveryAddressLine3} readOnly />
                  <input type="text" value={forms[0].deliveryAddressLine4} readOnly />
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Account Number</label>
                    <input type="text" value={forms[0].accountNumber} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Email Address</label>
                    <input type="text" value={forms[0].emailAddress} readOnly />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Home/Cell Number</label>
                    <input type="text" value={forms[0].homeCellNumber} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Employer (optional)</label>
                    <input type="text" value={forms[0].employer} readOnly />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Chronic Conditions</label>
                    <input type="text" value={forms[0].chronicConditions} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Beneficiaries (e.g. 4)</label>
                    <input type="text" value={forms[0].beneficiaries} readOnly />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Allergies</label>
                  <input type="text" value={forms[0].allergies} readOnly />
                </div>
              </div>

              <div className={styles.rightColumn}>
                <h3 className={styles.sectionTitle}>Medical Aid Details</h3>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Medical Aid Provider</label>
                    <input type="text" value={forms[0].medicalAidProvider} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Medical Aid Number</label>
                    <input type="text" value={forms[0].medicalAidNumber} readOnly />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Doctor</label>
                    <input type="text" value={practitionerName} readOnly />
                  </div>
                  <div className={styles.field}>
                    <label>Practice Number</label>
                    <input type="text" value={practiceNumber} readOnly />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Note</label>
                  <textarea value={forms[0].note} readOnly />
                </div>
              </div>
            </div>
          )}
          <div className={styles.buttonContainer}>
            <Button variant="primary" onClick={handleSplitProfile}>
              Split Profile
            </Button>
          </div>
        </div>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}